import { useCallback } from 'react';
import {
  TelemedLiveDiagnostics,
  TelemedLiveState,
  TelemedPatientBrief,
  TelemedSession,
  TelemedTranscriptSegment,
  TelemedTranscriptSpeaker,
  TelemedVisitStatus,
} from '../types/telemed';
import { useApiClient } from './apiClient';
import type { SupportedLanguage } from '../i18n/translations';

/**
 * Thin client for telemed endpoints. If backend is not ready, these can be
 * mocked in tests or wrapped with feature flags.
 */
export function useTelemedApi() {
  const { fetchApi, fetchJson } = useApiClient();

  const createTelemedSession = useCallback(
    async (appointmentId: string) => {
      return await fetchJson<TelemedSession>(`/appointments/${appointmentId}/telemed/session`, {
        method: 'POST',
      });
    },
    [fetchJson],
  );

  const updateVisitStatus = useCallback(
    async (appointmentId: string, visitStatus: TelemedVisitStatus) => {
      await fetchApi(`/appointments/${appointmentId}/telemed/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitStatus }),
      });
    },
    [fetchApi],
  );

  const endTelemedSession = useCallback(
    async (appointmentId: string) => {
      await fetchApi(`/appointments/${appointmentId}/telemed/end`, {
        method: 'POST',
      });
    },
    [fetchApi],
  );

  const getLiveSessionState = useCallback(
    async (sessionId: string) => {
      return await fetchJson<TelemedLiveState>(`/telemed/sessions/${sessionId}/live`);
    },
    [fetchJson],
  );

  const appendTranscriptChunk = useCallback(
    async (sessionId: string, payload: { text: string; speaker?: TelemedTranscriptSpeaker; replaceCurrent?: boolean }) => {
      return await fetchJson<{ transcriptText: string; transcriptUpdatedAt: string; transcriptSegments: TelemedTranscriptSegment[] }>(
        `/telemed/sessions/${sessionId}/transcript`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
    },
    [fetchJson],
  );

  const updateTranscriptSegment = useCallback(
    async (sessionId: string, segmentId: string, text: string) => {
      return await fetchJson<{ transcriptText: string; transcriptUpdatedAt: string; transcriptSegments: TelemedTranscriptSegment[] }>(
        `/telemed/sessions/${sessionId}/transcript/segments/${segmentId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        },
      );
    },
    [fetchJson],
  );

  const saveLiveNotes = useCallback(
    async (sessionId: string, clinicianNotes: string) => {
      return await fetchJson<{ clinicianNotes: string; updatedAt: string }>(`/telemed/sessions/${sessionId}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinicianNotes }),
      });
    },
    [fetchJson],
  );

  const getSessionPatientBrief = useCallback(
    async (sessionId: string) => {
      return await fetchJson<TelemedPatientBrief>(`/telemed/sessions/${sessionId}/patient-brief`);
    },
    [fetchJson],
  );

  const runLiveDiagnostics = useCallback(
    async (sessionId: string, payload?: { transcriptText?: string; clinicianNotes?: string; force?: boolean; locale?: SupportedLanguage }) => {
      return await fetchJson<TelemedLiveDiagnostics>(`/telemed/sessions/${sessionId}/diagnostics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
      });
    },
    [fetchJson],
  );

  return {
    createTelemedSession,
    updateVisitStatus,
    endTelemedSession,
    getLiveSessionState,
    appendTranscriptChunk,
    updateTranscriptSegment,
    saveLiveNotes,
    runLiveDiagnostics,
    getSessionPatientBrief,
  };
}
